/**
 * The roadmap: the chart, its toolbar, and the "still to decide" side panel.
 *
 * Was the whole of App.tsx until the Team page arrived. The masthead, the sign-out
 * button and the /api/me authorisation check moved up into AppShell, because both
 * pages need them and asking the API who you are twice per session is wasteful.
 *
 * ONE THING HERE IS NOT OBVIOUS AND IS DELIBERATE.
 *
 * The visible span is recomputed from the projects in state rather than taken from
 * the roadmap response's span_start/span_end. Editing a date changes the span, and
 * the response fields are a snapshot from before the edit - trusting them means
 * dragging a phase past the end of the chart and watching the bar vanish off the
 * right-hand edge.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import GapsPanel from '../components/GapsPanel';
import Legend from '../components/Legend';
import Timeline, { laneAnchorId } from '../components/chart/Timeline';
import { describeError, getGaps, getRoadmap } from '../services/api';
import { palette } from '../styles/theme';
import { Chip, ErrorText, Panel, SecondaryButton, ToggleButton } from '../styles/ui';
import type { Gap, GapReport, Phase, ProjectPatch, Roadmap } from '../types';
import { buildGrid, formatLong, todayISO } from '../utils/dates';
import { milestoneDates } from '../utils/milestones';

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

const Spacer = styled.div`
  flex: 1;
`;

const Columns = styled.div<{ $withPanel: boolean }>`
  display: grid;
  gap: 16px;
  grid-template-columns: ${(p) => (p.$withPanel ? 'minmax(0, 1fr) 360px' : 'minmax(0, 1fr)')};
  align-items: start;

  /* The side panel drops below the chart before the chart has to shrink: a Gantt
     narrower than its own minimum is unreadable, whereas a list of gaps reads fine
     at any width. */
  @media (max-width: 1180px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Status = styled.p`
  margin: 0;
  color: ${palette.inkSoft};
`;

const Summary = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
`;

export default function RoadmapPage() {
  const [roadmap, setRoadmap] = useState<Roadmap | null>(null);
  const [gaps, setGaps] = useState<GapReport | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
  const [showGaps, setShowGaps] = useState(true);
  const [includeInactive, setIncludeInactive] = useState(false);

  // Captured once per mount. Recomputed on every render it would be a new string
  // each time, so every memo below - including the grid - would rebuild constantly.
  const today = useMemo(() => todayISO(), []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [nextRoadmap, nextGaps] = await Promise.all([getRoadmap(includeInactive), getGaps()]);
      setRoadmap(nextRoadmap);
      setGaps(nextGaps);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }, [includeInactive]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Gaps change as a side effect of nearly every edit, so they are refreshed after one. */
  const refreshGaps = useCallback(async () => {
    try {
      setGaps(await getGaps());
    } catch {
      // A stale gap list is a much smaller problem than an error banner over a save
      // that actually succeeded. The next full load corrects it.
    }
  }, []);

  const onPhaseSaved = useCallback(
    (saved: Phase) => {
      setRoadmap((current) =>
        current
          ? {
              ...current,
              projects: current.projects.map((project) =>
                project.project_id === saved.project_id
                  ? {
                      ...project,
                      phases: project.phases.map((phase) =>
                        phase.phase_id === saved.phase_id ? saved : phase
                      ),
                    }
                  : project
              ),
            }
          : current
      );
      void refreshGaps();
    },
    [refreshGaps]
  );

  const onProjectSaved = useCallback(
    (projectId: string, patch: ProjectPatch) => {
      // Merged field by field. The PATCH response is ProjectOut and carries no
      // phases, so assigning it over the lane would empty it - the type forbids that
      // outright, and this is the reason why.
      setRoadmap((current) =>
        current
          ? {
              ...current,
              projects: current.projects.map((project) =>
                project.project_id === projectId ? { ...project, ...patch } : project
              ),
            }
          : current
      );
      void refreshGaps();
    },
    [refreshGaps]
  );

  const toggle = useCallback((projectId: string) => {
    setExpanded((current) => {
      const next = new Set(current);
      if (!next.delete(projectId)) {
        next.add(projectId);
      }
      return next;
    });
  }, []);

  const projects = useMemo(() => {
    const list = roadmap?.projects ?? [];
    // lane_order is the workbook's own row order, which is the order everyone
    // already has in their head. Name is only the tiebreak.
    return [...list].sort((a, b) => a.lane_order - b.lane_order || a.name.localeCompare(b.name));
  }, [roadmap]);

  const grid = useMemo(() => {
    const dates = projects.flatMap((project) => [
      ...project.phases.flatMap((phase) =>
        [phase.start, phase.end].filter((d): d is string => d !== null)
      ),
      // Milestone dates widen the span too. A regulatory deadline set past the last
      // phase is the single most important thing on the chart and must not fall off
      // the right-hand edge - that it sits beyond the work is precisely why it was
      // recorded. The backend's span_start/span_end already account for these; this
      // recomputation has to as well or the two disagree after an edit.
      ...milestoneDates(project.milestones),
    ]);
    // Nothing scheduled anywhere: still draw a grid, centred on today, so the
    // toolbar and the lanes have something to sit against instead of collapsing.
    const start = dates.length ? dates.reduce((a, b) => (a < b ? a : b)) : today;
    const end = dates.length ? dates.reduce((a, b) => (a > b ? a : b)) : today;
    return buildGrid(start, end, today);
  }, [projects, today]);

  const milestoneTotal = useMemo(
    () => projects.reduce((n, project) => n + project.milestones.length, 0),
    [projects]
  );

  const allExpanded = projects.length > 0 && expanded.size === projects.length;

  const onGapSelected = useCallback((gap: Gap) => {
    setExpanded((current) => new Set(current).add(gap.project_id));
    // Deferred a frame: the lane's phase rows do not exist until the state above has
    // rendered, so scrolling immediately targets the collapsed row and lands short.
    requestAnimationFrame(() => {
      document
        .getElementById(laneAnchorId(gap.project_id))
        ?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    });
  }, []);

  return (
    <>
      <Summary>
        <Chip title={formatLong(today)}>Today {formatLong(today)}</Chip>
        {roadmap ? (
          <Chip>
            {projects.length} projects ·{' '}
            {projects.reduce((n, p) => n + p.phases.filter((ph) => !ph.structural).length, 0)}{' '}
            phases
            {/* Milestones are counted alongside phases, never added to them: they are
                deadlines rather than work, and one total covering both would be a
                number that means nothing. Suppressed entirely at zero so the header
                does not advertise a feature nothing is using yet. */}
            {milestoneTotal > 0 ? ` · ${milestoneTotal} milestones` : ''}
          </Chip>
        ) : null}
      </Summary>

      <Toolbar>
        <SecondaryButton
          type="button"
          onClick={() =>
            setExpanded(allExpanded ? new Set() : new Set(projects.map((p) => p.project_id)))
          }
          disabled={projects.length === 0}
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </SecondaryButton>
        <ToggleButton
          type="button"
          $on={includeInactive}
          aria-pressed={includeInactive}
          onClick={() => setIncludeInactive((v) => !v)}
        >
          Include archived
        </ToggleButton>
        <ToggleButton
          type="button"
          $on={showGaps}
          aria-pressed={showGaps}
          onClick={() => setShowGaps((v) => !v)}
        >
          Still to decide{gaps ? ` (${gaps.gaps.length})` : ''}
        </ToggleButton>
        <SecondaryButton type="button" onClick={() => void load()} disabled={loading}>
          {loading ? 'Loading…' : 'Refresh'}
        </SecondaryButton>
        <Spacer />
        <Legend />
      </Toolbar>

      {error ? <ErrorText role="alert">{error}</ErrorText> : null}

      <Columns $withPanel={showGaps && gaps !== null}>
        <Panel>
          {loading && !roadmap ? (
            <Status>Loading the roadmap…</Status>
          ) : (
            <Timeline
              projects={projects}
              people={roadmap?.people ?? []}
              grid={grid}
              today={today}
              expanded={expanded}
              onToggle={toggle}
              onPhaseSaved={onPhaseSaved}
              onProjectSaved={onProjectSaved}
            />
          )}
        </Panel>

        {showGaps && gaps ? <GapsPanel report={gaps} onSelect={onGapSelected} /> : null}
      </Columns>
    </>
  );
}
